import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";

type AdminUser = {
  id: string;
  email: string;
  is_active: boolean;
  roles: string[];
  scopes: string[];
};

async function fetchUsers(): Promise<AdminUser[]> {
  const resp = await fetch(
    `${(import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api"}/admin/users`,
    { credentials: "include" },
  );
  if (!resp.ok) throw new ApiError(resp.status, undefined);
  return (await resp.json()) as AdminUser[];
}

export default function AdminPage() {
  const users = useQuery<AdminUser[]>({
    queryKey: ["admin", "users"],
    queryFn: fetchUsers,
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-brand-900">Admin</h1>
        <p className="text-sm text-slate-600 mt-1">
          Minimal Phase 3 admin surface: list users. Role/scope editing ships
          in the admin console refinement later in the phase.
        </p>
      </header>
      <section className="bg-white rounded-lg border shadow-sm p-5">
        <h2 className="font-medium">Users</h2>
        {users.isLoading && (
          <p className="text-sm text-slate-500 mt-2">Loading…</p>
        )}
        {users.isError && (
          <p className="text-sm text-rose-700 mt-2">
            Could not load users (check admin role).
          </p>
        )}
        {users.data && (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="py-2">Email</th>
                <th className="py-2">Active</th>
                <th className="py-2">Roles</th>
                <th className="py-2">Scopes</th>
              </tr>
            </thead>
            <tbody>
              {users.data.map((u) => (
                <tr key={u.id} className="border-b last:border-none">
                  <td className="py-2">{u.email}</td>
                  <td className="py-2">{u.is_active ? "yes" : "no"}</td>
                  <td className="py-2">{u.roles.join(", ") || "—"}</td>
                  <td className="py-2">{u.scopes.join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
